import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import Fade from "@mui/material/Fade"
import Modal from "@mui/material/Modal"
import Typography from "@mui/material/Typography"
import React from "react"

import close_icon from "../../assets/images/close_icon_new.svg"
import styles from "./muiModal.module.scss"

type Props = {
    open: boolean
    onClose: () => void
    children: React.ReactElement
    width: string
    padding?: string
    header?: string | React.ReactElement
    subHeader?: React.ReactNode
    headerTextColor?: string
    showFooter?: boolean
    maxHeight?: string
    onSave?: () => void
    submitButtonText?: string
    submitButtonFullWidth?: boolean
    submitButtonDisabled?: boolean
    cancelButtonText?: string
    showCloseIcon?: boolean
    disableCloseOnBackdropClick?: boolean
}

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bgcolor: "background.paper",
    borderRadius: "8px",
    outline: "none",
    maxWidth: "94vw",
    maxHeight: "94dvh",
    display: "flex",
    flexDirection: "column",
}

const MuiModal = ({ disableCloseOnBackdropClick = true, ...props }: Props) => {
    const handleClose = (_: any, reason: "backdropClick" | "escapeKeyDown") => {
        if (disableCloseOnBackdropClick && reason === "backdropClick") {
            return
        }
        props.onClose()
    }
    return (
        <Modal closeAfterTransition open={props.open} onClose={handleClose}>
            <Fade in={props.open}>
                <Box
                    sx={{
                        ...style,
                        width: { xs: "94vw", sm: props.width },
                        padding: props?.padding ? props.padding : "",
                    }}>
                    {(props.header || props?.showCloseIcon) && typeof props.header === "string" ? (
                        <Box
                            className={styles.header}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            gap="12px"
                            flexShrink={0}>
                            <Box lineHeight={"12px"} minWidth={0} flex={1}>
                                <Typography
                                    className="text-15 weight-500"
                                    color={props.headerTextColor}
                                    noWrap
                                    title={props.header}>
                                    {props.header}
                                </Typography>
                                {props.subHeader && (
                                    <Typography variant="caption" fontWeight="400" color="statiqOrange">
                                        {props.subHeader}
                                    </Typography>
                                )}
                            </Box>
                            <Box
                                component="button"
                                type="button"
                                onClick={props.onClose}
                                aria-label="Close"
                                sx={{
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "28px",
                                    height: "28px",
                                    padding: 0,
                                    border: "none",
                                    borderRadius: "50%",
                                    background: "transparent",
                                    cursor: "pointer",
                                    "&:hover": { background: "#F1F2F6" },
                                }}>
                                <img src={close_icon} alt="close_icon" width={12} height={12} />
                            </Box>
                        </Box>
                    ) : (
                        props.header
                    )}
                    <Box
                        maxHeight={{ xs: props.maxHeight || "100%", sm: props.maxHeight || "700px" }}
                        overflow={"auto"}
                        flexShrink={1}
                        className={styles.modalContent}>
                        {props.children}
                    </Box>
                    {props.showFooter && (
                        <Box
                            className={styles.footer}
                            display={"flex"}
                            gap={"8px"}
                            justifyContent={"flex-end"}
                            flexShrink={0}>
                            <Button variant="light" onClick={props.onClose} fullWidth={props.submitButtonFullWidth}>
                                {props.cancelButtonText ?? "Cancel"}
                            </Button>
                            <Button
                                variant="contained"
                                color="statiqOrange"
                                onClick={props.onSave}
                                fullWidth={props.submitButtonFullWidth}
                                disabled={props.submitButtonDisabled}>
                                {props.submitButtonText || "Submit "}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Fade>
        </Modal>
    )
}

export default MuiModal
